import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown, ShieldAlert, Flame, Leaf, GlassWater, Sparkles } from "lucide-react";

/** Collapsible card that lets the booker tell us their allergens, spice tolerance,
 * veg preference, and drink preferences — used to pre-filter the curated menu.
 * Persists choices in sessionStorage so the user only fills it once per session. */
export default function PreferenceCapture({ defaultAllergens = [], onChange, testid = "prefs" }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({ allergens: [], drink_preferences: [], veg_options: [] });
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = sessionStorage.getItem("tablo_prefs");
      return raw ? JSON.parse(raw) : { allergens: [], spice_level: 2, veg_preference: "mixed", veg_pax: 0, non_veg_pax: 0, drink_preferences: [], notes: "" };
    } catch (_) {
      return { allergens: [], spice_level: 2, veg_preference: "mixed", veg_pax: 0, non_veg_pax: 0, drink_preferences: [], notes: "" };
    }
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/meta");
        setMeta(data);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    sessionStorage.setItem("tablo_prefs", JSON.stringify(prefs));
    if (onChange) onChange(prefs);
  }, [prefs]); // eslint-disable-line

  const toggleAllergen = (a) => {
    setPrefs((p) => ({
      ...p,
      allergens: p.allergens.includes(a) ? p.allergens.filter((x) => x !== a) : [...p.allergens, a],
    }));
  };
  const toggleDrink = (d) => {
    setPrefs((p) => ({
      ...p,
      drink_preferences: p.drink_preferences.includes(d) ? p.drink_preferences.filter((x) => x !== d) : [...p.drink_preferences, d],
    }));
  };

  const allergens = meta.allergens?.length ? meta.allergens : ["nuts", "dairy", "gluten", "egg", "shellfish", "soy"];
  const drinkOptions = meta.drink_preferences?.length ? meta.drink_preferences : ["mocktails", "cocktails", "wine", "beer", "soft", "tea_coffee"];

  const summary = [
    prefs.allergens.length ? `Avoid: ${prefs.allergens.slice(0, 3).join(", ")}${prefs.allergens.length > 3 ? "…" : ""}` : null,
    prefs.veg_preference === "veg_only" ? "Veg only" : prefs.veg_preference === "non_veg_ok" ? "Non-veg OK" : "Mixed",
    `Spice ${prefs.spice_level ?? 2}/3`,
    prefs.drink_preferences.length ? `Drinks: ${prefs.drink_preferences.length}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="tablo-card p-4 space-y-3" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber" strokeWidth={1.6}/>
          <div>
            <p className="font-medium text-ink">Tell Tablo your preferences</p>
            <p className="text-xs text-light-ink mt-0.5">{summary}</p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-light-ink transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.6}/>
      </button>

      {open && (
        <div className="space-y-4 pt-2 border-t border-tablo-border">
          {/* Allergens */}
          <div>
            <p className="text-xs uppercase tracking-wider text-light-ink mb-2 flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 text-rose-600" strokeWidth={1.8}/> Allergens to avoid
            </p>
            <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-allergens`}>
              {allergens.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => toggleAllergen(a)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${prefs.allergens.includes(a) ? "bg-rose-600 text-white border-rose-600" : "bg-white text-ink border-tablo-border hover:border-rose-400"}`}
                  data-testid={`${testid}-allergen-${a}`}
                >
                  {a}
                </button>
              ))}
            </div>
            {defaultAllergens.length > 0 && (
              <p className="text-[11px] text-light-ink mt-1.5">
                This venue typically uses: <span className="text-ink font-medium">{defaultAllergens.join(", ")}</span>
              </p>
            )}
          </div>

          {/* Veg / Non-veg group split */}
          <div>
            <p className="text-xs uppercase tracking-wider text-light-ink mb-2 flex items-center gap-1.5">
              <Leaf className="w-3.5 h-3.5 text-green-700" strokeWidth={1.8}/> Group split — helps the chef portion right
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-light-ink mb-1 block">Vegetarian guests</label>
                <input
                  type="number"
                  min={0}
                  value={prefs.veg_pax || 0}
                  onChange={(e) => setPrefs((p) => ({ ...p, veg_pax: parseInt(e.target.value) || 0 }))}
                  className="w-full bg-white border border-tablo-border rounded-lg px-3 py-1.5 text-sm"
                  data-testid={`${testid}-veg-pax`}
                />
              </div>
              <div>
                <label className="text-[11px] text-light-ink mb-1 block">Non-vegetarian guests</label>
                <input
                  type="number"
                  min={0}
                  value={prefs.non_veg_pax || 0}
                  onChange={(e) => setPrefs((p) => ({ ...p, non_veg_pax: parseInt(e.target.value) || 0 }))}
                  className="w-full bg-white border border-tablo-border rounded-lg px-3 py-1.5 text-sm"
                  data-testid={`${testid}-non-veg-pax`}
                />
              </div>
            </div>
          </div>

          {/* Veg / Spice combined */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-light-ink mb-2 flex items-center gap-1.5">
                <Leaf className="w-3.5 h-3.5 text-green-700" strokeWidth={1.8}/> Veg preference
              </p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { v: "mixed", label: "Mixed" },
                  { v: "veg_only", label: "Veg only" },
                  { v: "non_veg_ok", label: "Non-veg OK" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setPrefs((p) => ({ ...p, veg_preference: opt.v }))}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${prefs.veg_preference === opt.v ? "bg-ink text-cream border-ink" : "bg-white text-ink border-tablo-border hover:border-amber/60"}`}
                    data-testid={`${testid}-veg-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-light-ink mb-2 flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5 text-rose-600" strokeWidth={1.8}/> Spice tolerance
              </p>
              <div className="flex gap-1.5">
                {[0, 1, 2, 3].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setPrefs((p) => ({ ...p, spice_level: s }))}
                    className={`text-xs flex-1 py-1.5 rounded-full border transition-colors ${prefs.spice_level === s ? "bg-rose-600 text-white border-rose-600" : "bg-white text-ink border-tablo-border hover:border-rose-300"}`}
                    data-testid={`${testid}-spice-${s}`}
                  >
                    {["None", "Mild", "Medium", "Hot"][s]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Drinks */}
          <div>
            <p className="text-xs uppercase tracking-wider text-light-ink mb-2 flex items-center gap-1.5">
              <GlassWater className="w-3.5 h-3.5 text-amber" strokeWidth={1.8}/> Drink preferences
            </p>
            <div className="flex flex-wrap gap-1.5">
              {drinkOptions.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDrink(d)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors capitalize ${prefs.drink_preferences.includes(d) ? "bg-amber text-ink border-amber" : "bg-white text-ink border-tablo-border hover:border-amber/60"}`}
                  data-testid={`${testid}-drink-${d}`}
                >
                  {d.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>
          {/* Anything else for Tablo to know? */}
          <div>
            <p className="text-xs uppercase tracking-wider text-light-ink mb-2">Any other preferences</p>
            <textarea
              rows={2}
              value={prefs.notes || ""}
              onChange={(e) => setPrefs((p) => ({ ...p, notes: e.target.value.slice(0, 280) }))}
              placeholder="e.g. one Jain guest, prefer biryani over rice, no eggs in dessert..."
              className="w-full bg-white border border-tablo-border rounded-lg px-3 py-2 text-sm"
              data-testid={`${testid}-notes`}
            />
          </div>
        </div>
      )}
    </div>
  );
}
